// Shared sync primitives used by every dual-mode local-first store
// (pilots / aircraft / flights / track_logs / ui_prefs).
//
// Each store keeps the local-first guarantee: writes hit IDB
// immediately so the UI stays fast and offline-safe. When a session
// is present, the same writes also fire a Supabase upsert/delete in
// the background. On first sign-in we push every local row up so
// the user doesn't lose anything they captured anonymously.
//
// The interface keeps each store's row mapping pluggable. Stores
// supply:
//   - tableName            'aircraft' | 'flights' | etc.
//   - upsertConflict       column expression used by ON CONFLICT
//   - toDbRow(local, ownerId)  client -> db row mapper
//   - fromDbRow(db)            db row -> client mapper
//
// Result: each store's sync layer is ~30 lines of glue.

import { supabase } from './supabase';
import { useAuthStore } from '../stores/authStore';
import { kvGet, kvSet } from './idb';

// ── Tombstone store — pending server deletes (OCC4) ──────────────────────────
//
// Problem: remove() deletes from IDB immediately. If the server delete
// fails (offline), the server still has the row. On reconnect, pullAll
// overwrites local IDB with the server's full row set — the 'deleted'
// row reappears as a ghost. Pilot un-assigns crew in the air, lands,
// ghost row comes back.
//
// Solution: tombstone the delete in IDB. pullAll and deleteRowByClientId
// both consult the tombstone list and filter ghosts out of the pulled
// server rows before writing to local IDB.

interface Tombstone {
  tableName: string;
  clientId: string;
  deletedAt: number; // Unix ms
}

const TOMBSTONE_KEY = 'fyrefly:tombstones:v1';
// Tombstones older than 30 days are pruned (server delete either
// succeeded eventually or the record is truly gone).
const TOMBSTONE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

let _tombstones: Tombstone[] | null = null;

async function getTombstones(): Promise<Tombstone[]> {
  if (_tombstones !== null) return _tombstones;
  const stored = await kvGet<Tombstone[]>(TOMBSTONE_KEY);
  _tombstones = Array.isArray(stored) ? stored : [];
  return _tombstones;
}

async function addTombstone(tableName: string, clientId: string): Promise<void> {
  const list = await getTombstones();
  if (list.some((t) => t.tableName === tableName && t.clientId === clientId)) return;
  const now = Date.now();
  // Prune stale tombstones before adding.
  const pruned = list.filter((t) => now - t.deletedAt < TOMBSTONE_MAX_AGE_MS);
  pruned.push({ tableName, clientId, deletedAt: now });
  _tombstones = pruned;
  await kvSet(TOMBSTONE_KEY, pruned);
}

async function removeTombstone(tableName: string, clientId: string): Promise<void> {
  const list = await getTombstones();
  const next = list.filter((t) => !(t.tableName === tableName && t.clientId === clientId));
  if (next.length === list.length) return;
  _tombstones = next;
  await kvSet(TOMBSTONE_KEY, next);
}

/** Filter server-pulled rows: remove any whose client_id has a pending
 *  tombstone (means we deleted locally but the server delete hasn't landed
 *  yet). Also attempts a flush-delete on each tombstone for this table. */
async function filterTombstoned<Local extends object>(
  rows: Local[],
  tableName: string,
  localIdField: keyof Local,
): Promise<Local[]> {
  const tombstones = await getTombstones();
  const pendingIds = new Set(
    tombstones
      .filter((t) => t.tableName === tableName)
      .map((t) => t.clientId),
  );
  if (pendingIds.size === 0) return rows;
  return rows.filter((r) => {
    const id = r[localIdField] as unknown as string;
    return !pendingIds.has(id);
  });
}

export interface SyncBindings<Local, DbRow extends object> {
  tableName: string;
  /** Comma-separated unique columns: 'owner_id,client_id' is the
   *  baseline for local-first sync. */
  upsertConflict: string;
  /** Local id field (string) on the local shape. */
  localIdField: keyof Local;
  toDbRow(local: Local, ownerId: string): DbRow;
  fromDbRow(row: DbRow): Local;
}

export interface SyncResult {
  ok: boolean;
  error?: string;
}

/** Push one row to the server. Fire-and-forget caller. */
export async function pushRow<Local extends object, DbRow extends object>(
  row: Local,
  b: SyncBindings<Local, DbRow>,
): Promise<SyncResult> {
  if (!supabase) return { ok: false, error: 'supabase not configured' };
  const user = useAuthStore.getState().user;
  if (!user) return { ok: false, error: 'not signed in' };
  try {
    const dbRow = b.toDbRow(row, user.id);
    const { error } = await supabase
      .from(b.tableName)
      .upsert(dbRow, { onConflict: b.upsertConflict });
    if (error) {
      // Loud console.error: previous behavior was fire-and-forget which
      // hid a silent 100%-failure of every push for the entire fleet of
      // dual-mode stores (see 2026-05-12 silent-sync incident).
      // eslint-disable-next-line no-console
      console.error(`[sync] pushRow ${b.tableName} failed:`, error.message, error);
      return { ok: false, error: error.message };
    }
    return { ok: true };
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.error(`[sync] pushRow ${b.tableName} threw:`, e);
    return { ok: false, error: e?.message ?? String(e) };
  }
}

/** Push many rows to the server. Used for first-sign-in migration
 *  and for setDefault flows that need a multi-row update in a
 *  single statement (partial unique index constraints). */
export async function pushMany<Local extends object, DbRow extends object>(
  rows: Local[],
  b: SyncBindings<Local, DbRow>,
): Promise<SyncResult> {
  if (!supabase) return { ok: false, error: 'supabase not configured' };
  const user = useAuthStore.getState().user;
  if (!user) return { ok: false, error: 'not signed in' };
  if (rows.length === 0) return { ok: true };
  try {
    const dbRows = rows.map((r) => b.toDbRow(r, user.id));
    const { error } = await supabase
      .from(b.tableName)
      .upsert(dbRows, { onConflict: b.upsertConflict });
    if (error) {
      // eslint-disable-next-line no-console
      console.error(`[sync] pushMany ${b.tableName} (${rows.length} rows) failed:`, error.message, error);
      return { ok: false, error: error.message };
    }
    return { ok: true };
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.error(`[sync] pushMany ${b.tableName} threw:`, e);
    return { ok: false, error: e?.message ?? String(e) };
  }
}

/** Delete one row by client_id. */
export async function deleteRowByClientId<Local extends object, DbRow extends object>(
  clientId: string,
  b: SyncBindings<Local, DbRow>,
): Promise<SyncResult> {
  // Add tombstone BEFORE the server call. If we're offline or the call
  // fails, the tombstone prevents pullAll from resurrecting the ghost row.
  await addTombstone(b.tableName, clientId);

  if (!supabase) return { ok: false, error: 'supabase not configured' };
  const user = useAuthStore.getState().user;
  if (!user) return { ok: false, error: 'not signed in' };
  try {
    const { error } = await supabase
      .from(b.tableName)
      .delete()
      .eq('owner_id', user.id)
      .eq('client_id', clientId);
    if (error) {
      // eslint-disable-next-line no-console
      console.error(`[sync] deleteRowByClientId ${b.tableName} failed:`, error.message, error);
      // Keep the tombstone so pullAll can filter the ghost on reconnect.
      return { ok: false, error: error.message };
    }
    // Server delete succeeded — remove tombstone.
    await removeTombstone(b.tableName, clientId);
    return { ok: true };
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.error(`[sync] deleteRowByClientId ${b.tableName} threw:`, e);
    // Keep the tombstone for ghost prevention on reconnect.
    return { ok: false, error: (e as Error)?.message ?? String(e) };
  }
}

/** Pull every row this user owns. Local-first stores call this on
 *  hydrate when a session already exists, and on the
 *  signed-out->signed-in auth transition. */
export async function pullAll<Local extends object, DbRow extends object>(
  b: SyncBindings<Local, DbRow>,
): Promise<{ ok: boolean; rows: Local[]; error?: string }> {
  if (!supabase) return { ok: false, rows: [], error: 'supabase not configured' };
  const user = useAuthStore.getState().user;
  if (!user) return { ok: false, rows: [], error: 'not signed in' };
  try {
    // Flush any pending tombstoned deletes for this table first.
    // If the server delete succeeds, the tombstone is removed; if it
    // fails (still offline), we filter it out of pulled rows below.
    const tombstones = await getTombstones();
    const pendingDeletes = tombstones.filter((t) => t.tableName === b.tableName);
    for (const t of pendingDeletes) {
      try {
        const { error: delErr } = await supabase
          .from(b.tableName)
          .delete()
          .eq('owner_id', user.id)
          .eq('client_id', t.clientId);
        if (!delErr) await removeTombstone(b.tableName, t.clientId);
      } catch { /* still offline — tombstone stays */ }
    }

    const { data, error } = await supabase
      .from(b.tableName)
      .select('*')
      .eq('owner_id', user.id);
    if (error) {
      // eslint-disable-next-line no-console
      console.error(`[sync] pullAll ${b.tableName} failed:`, error.message, error);
      return { ok: false, rows: [], error: error.message };
    }
    // Filter any rows the user has tombstoned locally (server delete may
    // not have landed yet) so ghost rows don't reappear after reconnect.
    const allRows = (data as DbRow[]).map(b.fromDbRow);
    const filtered = await filterTombstoned(allRows, b.tableName, b.localIdField);
    return { ok: true, rows: filtered };
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.error(`[sync] pullAll ${b.tableName} threw:`, e);
    return { ok: false, rows: [], error: (e as Error)?.message ?? String(e) };
  }
}
